import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Star, FileText, BellRing } from "lucide-react";

const SCHEDULER_ROLES = ["recruiter", "hiring_manager", "group_hr_head", "entity_hr_head", "super_admin"];

function Stars({ n }) {
  return <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={12} className={i <= n ? "fill-amber-400 text-amber-400" : "text-gray-300"} />)}</span>;
}

export default function Interviews() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const candParam = params.get("candidate") || "";
  const [ivs, setIvs] = useState([]);
  const [cands, setCands] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [scoreFor, setScoreFor] = useState(null);
  const [form, setForm] = useState({ candidate_id: "", round_name: "Technical Round 1", round_type: "technical", scheduled_at: "", duration_mins: 60, mode: "video", panelists: "Vikram Singh" });
  const [card, setCard] = useState({ overall_rating: 4, technical_skills: 4, communication: 4, culture_fit: 4, strengths: "", concerns: "", recommendation: "hire" });
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState("list");
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [schedFor, setSchedFor] = useState(null);
  const [sched, setSched] = useState({ scheduled_at: "", duration_mins: 45, mode: "video", panelists: "Vikram Singh" });
  const [notesFor, setNotesFor] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [teamsBusy, setTeamsBusy] = useState(false);

  const runNotes = async (ivId) => {
    if (transcript.trim().length < 50) return toast.error("Paste a longer transcript");
    setTeamsBusy(true);
    try {
      const { data } = await api.post("/automation/teams/notes", { interview_id: ivId, transcript });
      toast.success(`AI note-taker draft ready — rating ${data.notes.draft.overall_rating}/5`);
      setCard({
        overall_rating: data.notes.draft.overall_rating,
        technical_skills: data.notes.draft.technical_skills,
        communication: data.notes.draft.communication,
        culture_fit: data.notes.draft.role_fit,
        strengths: (data.notes.draft.strengths || []).join(", "),
        concerns: (data.notes.draft.concerns || []).join(", "),
        recommendation: data.notes.draft.recommendation,
      });
      setScoreFor(ivId); setNotesFor(null); setTranscript("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTeamsBusy(false); }
  };

  const runReminders = async () => {
    try {
      const { data } = await api.post("/automation/reminders/interview-feedback", { sla_hours: 24 });
      toast.success(`${data.reminders_sent} reminder(s) sent · ${data.escalated} escalated`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doSchedule = async (ivId) => {
    if (!sched.scheduled_at) return toast.error("Pick date & time");
    try {
      const { data } = await api.post(`/interviews/${ivId}/reschedule`, {
        ...sched, duration_mins: +sched.duration_mins,
        panelists: sched.panelists.split(",").map((p) => p.trim()).filter(Boolean),
      });
      if (data.conflicts?.length) {
        toast.warning(`Scheduled, but panelist conflict: ${data.conflicts[0].candidate_name} at ${new Date(data.conflicts[0].scheduled_at).toLocaleString()}`);
      } else {
        toast.success("Interview scheduled");
      }
      setSchedFor(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadIcs = async (ivId) => {
    try {
      const res = await api.get(`/interviews/${ivId}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `interview-${ivId.slice(0, 8)}.ics`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download invite"); }
  };

  const load = () => api.get("/interviews").then((r) => setIvs(r.data));
  useEffect(() => {
    load();
    api.get("/candidates").then((r) => {
      setCands(r.data);
      if (r.data[0]) setForm((f) => ({ ...f, candidate_id: r.data[0].id }));
    }).catch(() => {});
  }, []);

  const schedule = async () => {
    const cand = cands.find((c) => c.id === form.candidate_id);
    if (!cand || !form.scheduled_at) return toast.error("Candidate and date/time required");
    try {
      await api.post("/interviews", { ...form, requisition_id: cand.requisition_id, duration_mins: +form.duration_mins, panelists: form.panelists.split(",").map((p) => p.trim()).filter(Boolean) });
      toast.success("Interview scheduled");
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitCard = async () => {
    try {
      await api.post(`/interviews/${scoreFor}/scorecard`, { ...card, overall_rating: +card.overall_rating, technical_skills: +card.technical_skills, communication: +card.communication, culture_fit: +card.culture_fit });
      toast.success("Scorecard submitted");
      setScoreFor(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canSchedule = SCHEDULER_ROLES.includes(user?.role);
  const canScore = canSchedule || user?.role === "interview_panelist";

  const filteredIvs = ivs.filter((iv) => (!statusFilter || iv.status === statusFilter) && (!candParam || iv.candidate_id === candParam));
  const pages = Math.max(1, Math.ceil(filteredIvs.length / 6));
  const pageIvs = filteredIvs.slice((Math.min(page, pages) - 1) * 6, Math.min(page, pages) * 6);

  return (
    <div data-testid="interviews-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">Evaluation</div>
          <h1 className="font-heading font-black text-3xl tracking-tight mt-1">Interviews</h1>
        </div>
        {canSchedule && (
          <div className="flex gap-2">
            <button data-testid="run-reminders-button" onClick={runReminders}
              className="flex items-center gap-1.5 border border-[#0A251D] text-[#0A251D] px-3 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#0A251D] hover:text-white transition-colors duration-150">
              <BellRing size={13} /> Run Reminders
            </button>
            <button data-testid="schedule-interview-button" onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 bg-[#0A251D] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">
              <Plus size={14} /> Schedule Interview
            </button>
          </div>
        )}
      </div>

      {showForm && (
        <div className="border bg-white rounded-xl p-6 mt-5 grid md:grid-cols-3 gap-4" data-testid="interview-form">
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Candidate</label>
            <select data-testid="iv-candidate-select" value={form.candidate_id} onChange={(e) => setForm({ ...form, candidate_id: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm bg-white">
              {cands.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.stage})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Round Name</label>
            <input value={form.round_name} onChange={(e) => setForm({ ...form, round_name: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Type</label>
            <select value={form.round_type} onChange={(e) => setForm({ ...form, round_type: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm bg-white">
              {["technical", "hr", "managerial", "culture"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Date & Time</label>
            <input data-testid="iv-datetime-input" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Mode</label>
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm bg-white">
              {["video", "in_person", "phone"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Panelists (comma-separated)</label>
            <input value={form.panelists} onChange={(e) => setForm({ ...form, panelists: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
          </div>
          <div className="md:col-span-3 flex gap-3">
            <button data-testid="iv-save-button" onClick={schedule} className="bg-[#0A251D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Schedule</button>
            <button onClick={() => setShowForm(false)} className="border px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-secondary transition-colors duration-150">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="interviews-filter-bar">
        <div className="flex rounded-lg border overflow-hidden mr-2">
          {[["list", "List"], ["calendar", "Calendar"]].map(([v, l]) => (
            <button key={v} data-testid={`interviews-view-${v}`} onClick={() => setView(v)}
              className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-none transition-colors duration-150 ${view === v ? "bg-[#0A251D] text-white" : "bg-white hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
        {["", "pending_schedule", "scheduled", "completed", "cancelled"].map((s) => (
          <button key={s || "all"} data-testid={`interviews-filter-${s || "all"}`}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-full border transition-colors duration-150 ${
              statusFilter === s ? "bg-[#0A251D] text-white border-[#0A251D]" : "bg-white hover:border-[#0A251D]"}`}>
            {s ? s.replace("_", " ") : "All"} ({s ? ivs.filter((i) => i.status === s).length : ivs.length})
          </button>
        ))}
        {candParam && (
          <button data-testid="clear-candidate-filter" onClick={() => setParams({})}
            className="text-xs font-bold uppercase tracking-wider bg-[#0A251D] text-white px-3.5 py-1.5 rounded-full hover:bg-[#133E31] transition-colors duration-150">
            Candidate: {ivs.find((i) => i.candidate_id === candParam)?.candidate_name || "filtered"} ✕
          </button>
        )}
      </div>

      {view === "calendar" && (
        <div className="border bg-white rounded-xl p-5 mt-4" data-testid="interviews-calendar">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="cal-prev" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))} className="border px-3 py-1.5 text-xs font-bold hover:bg-secondary transition-colors duration-150">← Prev</button>
            <div className="font-heading font-bold text-lg">{calMonth.toLocaleString("default", { month: "long", year: "numeric" })}</div>
            <button data-testid="cal-next" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))} className="border px-3 py-1.5 text-xs font-bold hover:bg-secondary transition-colors duration-150">Next →</button>
          </div>
          <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="bg-[#F2F5F1] px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{d}</div>
            ))}
            {(() => {
              const first = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
              const days = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate();
              const cells = [];
              for (let i = 0; i < first.getDay(); i++) cells.push(<div key={`e${i}`} className="bg-white min-h-[76px]" />);
              for (let d = 1; d <= days; d++) {
                const dayIvs = ivs.filter((iv) => {
                  if (!iv.scheduled_at) return false;
                  const dt = new Date(iv.scheduled_at);
                  return dt.getFullYear() === calMonth.getFullYear() && dt.getMonth() === calMonth.getMonth() && dt.getDate() === d;
                });
                cells.push(
                  <div key={d} className="bg-white min-h-[76px] p-1.5">
                    <div className="text-[11px] font-bold text-muted-foreground">{d}</div>
                    {dayIvs.map((iv) => (
                      <div key={iv.id} title={`${iv.candidate_name} — ${iv.round_name}`}
                        className={`mt-1 px-1.5 py-1 rounded text-[10px] font-semibold truncate ${iv.status === "completed" ? "bg-[#DDEAE3] text-[#133E31]" : iv.status === "cancelled" ? "bg-gray-200 text-gray-500 line-through" : "bg-[#0A251D] text-white"}`}>
                        {new Date(iv.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} {iv.candidate_name.split(" ")[0]} · {iv.round_name}
                      </div>
                    ))}
                  </div>
                );
              }
              return cells;
            })()}
          </div>
        </div>
      )}

      {view === "list" && (
      <div className="space-y-3 mt-4">
        {pageIvs.map((iv) => (
          <div key={iv.id} className="border bg-white rounded-xl p-5" data-testid={`interview-card-${iv.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="font-semibold">
                  <Link to={`/candidates/${iv.candidate_id}`} data-testid={`iv-candidate-link-${iv.id}`} className="text-[#0A251D] hover:text-[#E05A47] transition-colors duration-100">{iv.candidate_name}</Link>
                  {" — "}{iv.round_name}
                  {iv.round_order && <span className="ml-2 text-[10px] uppercase tracking-wider bg-secondary px-2 py-0.5 rounded font-bold align-middle">Round {iv.round_order}/{iv.total_rounds}</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 capitalize">
                  {iv.round_type} · {iv.mode?.replace("_", " ")} · {iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString() : "awaiting schedule"} · {iv.duration_mins} mins · Panel: {iv.panelists?.join(", ") || "TBD"}
                  {" · "}
                  <Link to={`/requisitions/${iv.requisition_id}`} className="text-[#0A251D] hover:text-[#E05A47] normal-case font-semibold transition-colors duration-100">View requisition →</Link>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${
                  iv.status === "scheduled" ? "bg-[#DDEAE3] text-[#133E31]" : iv.status === "completed" ? "bg-emerald-100 text-emerald-800" : iv.status === "pending_schedule" ? "bg-amber-100 text-amber-800" : "bg-gray-200 text-gray-600"}`}>{iv.status.replace("_", " ")}</span>
                {iv.status === "scheduled" && (
                  <button data-testid={`ics-download-${iv.id}`} onClick={() => downloadIcs(iv.id)}
                    className="text-xs border px-3 py-1.5 hover:border-[#0A251D] transition-colors duration-150">📅 .ics invite</button>
                )}
                {canSchedule && iv.status === "pending_schedule" && (
                  <button data-testid={`schedule-round-${iv.id}`} onClick={() => setSchedFor(schedFor === iv.id ? null : iv.id)}
                    className="text-xs bg-[#E05A47] text-white px-3 py-1.5 font-bold hover:bg-[#C74936] transition-colors duration-150">Schedule Round</button>
                )}
                {canScore && iv.status === "scheduled" && (
                  <>
                    <button data-testid={`teams-notes-${iv.id}`} onClick={() => setNotesFor(notesFor === iv.id ? null : iv.id)}
                      className="text-xs border px-3 py-1.5 hover:border-[#0A251D] transition-colors duration-150 flex items-center gap-1"><FileText size={12} /> Teams Notes</button>
                    <button data-testid={`submit-scorecard-${iv.id}`} onClick={() => setScoreFor(scoreFor === iv.id ? null : iv.id)}
                      className="text-xs border border-[#0A251D] text-[#0A251D] px-3 py-1.5 hover:bg-[#0A251D] hover:text-white transition-colors duration-150">Submit Scorecard</button>
                  </>
                )}
              </div>
            </div>
            {schedFor === iv.id && (
              <div className="mt-4 border-t pt-4 flex flex-wrap items-end gap-3" data-testid={`schedule-form-${iv.id}`}>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Date & Time</label>
                  <input data-testid="round-datetime-input" type="datetime-local" value={sched.scheduled_at} onChange={(e) => setSched({ ...sched, scheduled_at: e.target.value })} className="mt-1 block border px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Duration (mins)</label>
                  <input type="number" value={sched.duration_mins} onChange={(e) => setSched({ ...sched, duration_mins: e.target.value })} className="mt-1 block border px-3 py-2 text-sm w-24" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Panelists (comma-sep)</label>
                  <input value={sched.panelists} onChange={(e) => setSched({ ...sched, panelists: e.target.value })} className="mt-1 block border px-3 py-2 text-sm w-56" />
                </div>
                <button data-testid="round-schedule-save" onClick={() => doSchedule(iv.id)}
                  className="bg-[#0A251D] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Confirm Slot</button>
              </div>
            )}
            {iv.teams_notes && iv.teams_notes.draft && (
              <div className="mt-3 border-t pt-3 text-xs" data-testid={`teams-notes-summary-${iv.id}`}>
                <div className="font-bold text-[#0A251D] uppercase tracking-widest text-[10px] mb-1">AI Note-Taker Summary</div>
                <div className="text-sm text-muted-foreground">{iv.teams_notes.draft.summary}</div>
              </div>
            )}
            {iv.scorecards?.map((sc, i) => (
              <div key={i} className="mt-3 border-t pt-3 text-sm">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-medium">{sc.panelist}</span>
                  <Stars n={sc.overall_rating} />
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${sc.recommendation.includes("no") ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-800"}`}>{sc.recommendation.replace("_", " ")}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">Tech {sc.technical_skills}/5 · Comms {sc.communication}/5 · Culture {sc.culture_fit}/5</div>
                {sc.strengths && <div className="text-xs mt-1">+ {sc.strengths}</div>}
                {sc.concerns && <div className="text-xs">− {sc.concerns}</div>}
              </div>
            ))}
            {scoreFor === iv.id && (
              <div className="mt-4 border-t pt-4 grid md:grid-cols-4 gap-3" data-testid="scorecard-form">
                {[["overall_rating", "Overall"], ["technical_skills", "Technical"], ["communication", "Communication"], ["culture_fit", "Culture Fit"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">{l} (1–5)</label>
                    <input type="number" min="1" max="5" value={card[k]} onChange={(e) => setCard({ ...card, [k]: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Strengths</label>
                  <input data-testid="scorecard-strengths-input" value={card.strengths} onChange={(e) => setCard({ ...card, strengths: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Concerns</label>
                  <input value={card.concerns} onChange={(e) => setCard({ ...card, concerns: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Recommendation</label>
                  <select value={card.recommendation} onChange={(e) => setCard({ ...card, recommendation: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm bg-white">
                    {["strong_hire", "hire", "no_hire", "strong_no_hire"].map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
                <div className="md:col-span-4">
                  <button data-testid="scorecard-submit-button" onClick={submitCard} className="bg-[#0A251D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Submit</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {filteredIvs.length === 0 && <div className="text-sm text-muted-foreground border bg-white rounded-xl p-6">No interviews match this filter.</div>}
      </div>
      )}
      {view === "list" && pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-4">
          <button data-testid="interviews-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border bg-white px-3 py-1.5 text-xs rounded-lg disabled:opacity-40 hover:border-[#0A251D] transition-colors duration-100">Prev</button>
          <span className="text-xs text-muted-foreground">Page {Math.min(page, pages)} of {pages}</span>
          <button data-testid="interviews-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border bg-white px-3 py-1.5 text-xs rounded-lg disabled:opacity-40 hover:border-[#0A251D] transition-colors duration-100">Next</button>
        </div>
      )}
    </div>
  );
}
